/**
 * SalesforceEntitySearch — search Salesforce borrowing entities by name and
 * pre-fill the surrounding React Final Form from a pick.
 *
 * POST /underwriting/search_salesforce_entities { entityName } (min 3 chars) →
 * Account records keyed by Account Id, each with entityName / entityEINNumber /
 * integrationId / salesforceAccountId and prior EV/LQ loan history
 * (sf_opportunities). Members/guarantors are NOT included — completed on selection.
 *
 * Drop it inside any RffForm (it uses `useForm().change` to fill fields). Only
 * fields the lookup returns are filled; the user completes the rest. Pass
 * `namePrefix` (e.g. "borrowerEntity.") when the entity fields are nested.
 */
import { useState } from "react";
import { useForm } from "react-final-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import DomainOutlined from "@mui/icons-material/DomainOutlined";
import ManageSearchOutlined from "@mui/icons-material/ManageSearchOutlined";
import size from "lodash/size";

import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";

// Map a Salesforce result onto the entity form. Mirrors the backend's canonical
// build_entity() so a selected entity is fully linked to Salesforce: the name +
// EIN are visible fields; the SF ids are carried as hidden values that submit +
// persist (no visible input, but they save with the entity).
const mapSfEntityToValues = (entity) => ({
  entityName: entity?.entityName || "",
  entityEINNumber: entity?.entityEINNumber || "",
  integration_id: entity?.integrationId || "",
  salesforce_account_id: entity?.salesforceAccountId || "",
  salesforceId: entity?.salesforceAccountId || "",
});

/**
 * The search itself, with NO react-final-form binding — `onSelect` gets the
 * mapped values. The panel-form sidebars are controlled, so they use this
 * one directly; the RFF wrapper below keeps every existing caller working.
 */
export function SalesforceEntitySearchBase({ sx, onSelect }) {
  const { searchSalesforceEntities } = useUnderwritingHook();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null); // object keyed by Account Id, or null
  const [searching, setSearching] = useState(false);

  const canSearch = term.trim().length >= 3;

  const handleSearch = () => {
    if (!canSearch) return;
    setSearching(true);
    setResults(null);
    searchSalesforceEntities({
      data: { entityName: term.trim() },
      onSuccessFn: (res) => setResults(res || {}),
      onCompleteFn: () => setSearching(false),
      onFailFn: () => setSearching(false),
    });
  };

  const handleSelect = (entity) => {
    onSelect(mapSfEntityToValues(entity));
    setResults(null);
    setTerm("");
  };

  const entries = results ? Object.entries(results) : [];

  return (
    <Box
      sx={{
        // mb: 2,
        mb: 4,
        p: 1.5,
        border: "1px dashed #cbd5e1",
        borderRadius: "8px",
        background: "#f8fafc",
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <ManageSearchOutlined sx={{ fontSize: 18, color: "#475569" }} />
        <Box sx={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>
          Search Salesforce entities
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Entity name (min 3 characters)"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleSearch}
          disabled={!canSearch || searching}
          startIcon={
            searching ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Search
        </Button>
      </Box>

      {results && (
        <Box sx={{ mt: 1 }}>
          {size(results) === 0 ? (
            <Box sx={{ fontSize: 12, color: "#b45309" }}>
              No Salesforce entities found for “{term || "that name"}”.
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {entries.map(([accountId, entity]) => (
                <Box
                  key={accountId}
                  component="button"
                  type="button"
                  onClick={() => handleSelect(entity)}
                  sx={{
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: "8px",
                    border: "1px solid #e3e9ec",
                    background: "#fff",
                    "&:hover": {
                      background: "#f1f7ff",
                      borderColor: "#0a66ff",
                    },
                  }}
                >
                  <DomainOutlined
                    sx={{ fontSize: 18, color: "#4338ca", flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {entity?.entityName || "Unnamed entity"}
                    </Box>
                    <Box sx={{ fontSize: 11, color: "#64748b" }}>
                      {[
                        entity?.entityEINNumber
                          ? `EIN ${entity.entityEINNumber}`
                          : null,
                        entity?.mostFrequentLoanAdvisor || null,
                        `${size(entity?.sf_opportunities)} prior loan${
                          size(entity?.sf_opportunities) === 1 ? "" : "s"
                        }`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Box>
                    {/* Prior EV/LQ loan numbers — context for disambiguating
                        same-named entities (EINs are usually null). */}
                    {size(entity?.sf_opportunities) > 0 && (
                      <Box
                        sx={{
                          fontSize: 10.5,
                          color: "#94a3b8",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {Object.keys(entity.sf_opportunities).join(", ")}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * RFF wrapper — writes the pick straight into the surrounding form. `useForm`
 * is called HERE, not in the base, so the base can render outside a form.
 */
export default function SalesforceEntitySearch({ sx, namePrefix = "" }) {
  const form = useForm();
  return (
    <SalesforceEntitySearchBase
      sx={sx}
      onSelect={(values) =>
        Object.entries(values).forEach(([k, v]) => {
          if (v) form.change(`${namePrefix}${k}`, v);
        })
      }
    />
  );
}
